import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Phone, Mail, Minimize2 } from 'lucide-react';
import axios from 'axios';

// Usar URL del backend. En producción, usar el mismo origen para evitar valores baked-in.
const BACKEND_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000')
  : window.location.origin;

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hola, soy Clara. Cuéntame cómo puedo ayudarte el día de hoy.',
      suggestedActions: [
        '📊 Ver paquetes disponibles',
        '💼 Conocer servicios',
        '📝 Solicitar cotización'
      ]
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputValue.trim();
    if (!textToSend) return;

    // Agregar mensaje del usuario
    const userMessage = {
      role: 'user',
      content: textToSend
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: textToSend,
        conversation_history: messages.slice(-6), // Últimos 6 mensajes
        user_info: userInfo
      });

      const botMessage = {
        role: 'assistant',
        content: response.data.response,
        suggestedActions: response.data.suggested_actions
      };

      setMessages(prev => [...prev, botMessage]);

      // Si capturó lead, guardar
      if (response.data.lead_captured && Object.values(userInfo).some(v => v)) {
        await axios.post(`${BACKEND_URL}/api/chat/lead`, {
          ...userInfo,
          last_message: textToSend,
          conversation: messages.slice(-4)
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Backend URL:', BACKEND_URL);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.detail || error.message || 'Lo siento, tuve un problema técnico.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `😕 ${errorMessage}\n\n¿Puedes intentar de nuevo o contactarnos directamente al contacto@sinergiaempresarialcsce.com?`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    const actionMap = {
      '📊 Ver paquetes disponibles': '¿Cuáles son los paquetes disponibles?',
      '💼 Conocer servicios': '¿Qué servicios ofrecen?',
      '💼 Conocer servicios detallados': '¿Qué servicios ofrecen en detalle?',
      '📞 Agendar consulta': 'Quiero agendar una consulta',
      '📝 Solicitar cotización': 'Quiero solicitar una cotización personalizada',
      '📅 Agendar consulta gratuita': 'Quiero agendar una consulta gratuita',
      '💬 Contactar por WhatsApp': 'WhatsApp',
      '📝 Ir a contacto': 'Ir a contacto'
    };

    const message = actionMap[action] || action;
    
    if (action.includes('WhatsApp')) {
      window.open('https://wa.me/573175111422?text=Hola, vengo del sitio web de Sinergia Empresarial', '_blank');
    } else if (action.includes('contacto')) {
      window.open('https://sinergiaempresarialcsce.com/contacto', '_blank');
    } else {
      handleSendMessage(message);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-sinergia-azul hover:bg-sinergia-verde-claro text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-50 group"
        aria-label="Abrir chat"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-sinergia-gris-oscuro text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          ¿Necesitas ayuda? 💬
        </span>
        <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl z-50 flex flex-col transition-all duration-300 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      }`}
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <span className="text-sinergia-azul font-bold text-lg">SC</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm">Sinergia Empresarial</h3>
            <p className="text-xs text-white text-opacity-90">Asistente CFO • En línea 🟢</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="hover:bg-sinergia-verde hover:bg-opacity-50 p-1.5 rounded-lg transition-colors"
            aria-label={isMinimized ? 'Maximizar' : 'Minimizar'}
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="hover:bg-sinergia-verde hover:bg-opacity-50 p-1.5 rounded-lg transition-colors"
            aria-label="Cerrar chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-sinergia-azul text-white'
                      : 'bg-white text-gray-800 shadow-md border border-gray-100'
                  }`}
                >
                  <div
                    className="text-sm whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: message.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>')
                    }}
                  />
                  
                  {/* Suggested Actions */}
                  {message.suggestedActions && message.suggestedActions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.suggestedActions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleQuickAction(action)}
                          className="block w-full text-left px-3 py-2 bg-gray-50 hover:bg-sinergia-azul hover:text-white text-sinergia-gris-oscuro text-xs rounded-lg transition-colors border border-sinergia-gris-claro"
                        >
                          {action}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-md">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Contact Info */}
          <div className="px-4 py-2 bg-gray-50 border-t border-sinergia-gris-claro flex items-center justify-between text-xs">
            <a href="tel:+573175111422" className="flex items-center text-sinergia-azul hover:text-sinergia-verde">
              <Phone className="w-3 h-3 mr-1" />
              <span>+57 317 511 1422</span>
            </a>
            <a href="mailto:contacto@sinergiaempresarialcsce.com" className="flex items-center text-sinergia-azul hover:text-sinergia-verde">
              <Mail className="w-3 h-3 mr-1" />
              <span>Email</span>
            </a>
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200 rounded-b-2xl">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sinergia-azul focus:border-transparent text-sm"
                disabled={isLoading}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputValue.trim()}
                className="bg-sinergia-azul hover:bg-sinergia-verde-claro text-white p-2.5 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Enviar mensaje"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Respuestas en horario laboral (Lun-Vie 8AM-6PM)
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatbotWidget;
